/**
 * src/components/pages/PromoDetailPage.js
 *
 * Detalle de una promoción. Lee :id de los params del router,
 * muestra imagen grande, info completa y CTA WhatsApp + Ver negocio.
 */

import '@/styles/promo-detail.css'
import { define, c } from '@components/Component.js'
import { h } from '@core/dom.js'
import { promosData, TAG_COLORS } from '@/db/promos.db.js'
import { getById } from '@/db/businesses.db.js'
import { trackClick } from '@/shared/utils/track.js'
import { brandIcons, uiIcons } from '@/shared/brandIcons.js'
import { waLink } from '@/shared/utils/whatsapp.js'

function parseDescription(text) {
    const contactKeys = /^(direcci[oó]n|celular|whatsapp|tel[eé]fono|contacto|servicios|instagram|facebook|tiktok|barrio|redes\s+sociales|comun[ií]ca)[\s\w\/]*[:/]/i
    const contactEmoji = /^[📍📱📞☎📲🏠🏪📧📌🗺]\s*/u
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean)
    const desc = [], contact = []
    for (const line of lines) {
        if (contactKeys.test(line) || contactEmoji.test(line)) contact.push(line)
        else desc.push(line)
    }
    return { desc: desc.join('\n'), contact }
}

define('PromoDetailPage', ({ params = {} }) => {
    const promo = promosData.items.find(p => p.id === Number(params.id))

    if (promo) trackClick('promo', promo.id)

    if (!promo) {
        return h('div', { className: 'page' },
            c('Header'),
            h('div', { className: 'promo-detail-page' },
                h('div', { className: 'promo-detail__inner' },
                    h('a', { href: '/promociones', className: 'promo-detail__back' }, '← Volver a promociones'),
                    h('p', {}, '404 — Promoción no encontrada.')
                )
            )
        )
    }

    const business = getById(promo.businessId)
    const color    = TAG_COLORS[promo.tag] || { bg: '#f3f4f6', color: '#374151' }
    const parsed   = parseDescription(promo.description || '')

    /* Badge de tag */
    const tagBadge = h('span', { className: 'promo-detail__tag' }, promo.tag)
    tagBadge.style.background = color.bg
    tagBadge.style.color      = color.color

    /* Hero image */
    const heroSection = h('div', { className: 'promo-detail__hero' },
        h('img', {
            src:       promo.image,
            alt:       promo.title,
            className: 'promo-detail__hero-img'
        }),
        tagBadge
    )

    /* Cuerpo — solo descripción */
    const body = h('div', { className: 'promo-detail__body' },
        business
            ? h('a', {
                href:      `/negocio/${promo.businessId}`,
                className: 'promo-detail__business-link',
                innerHTML: uiIcons.store + ' ' + promo.business + ' →' })
            : h('span', { className: 'promo-detail__business-label', innerHTML: uiIcons.store + ' ' + promo.business }),
        h('h1', { className: 'promo-detail__title' }, promo.title),
        h('div', { className: 'promo-detail__desc' },
            ...parsed.desc.split('\n').filter(l => l.trim()).map(line =>
                h('p', { className: 'promo-detail__desc-line' }, line)
            )
        ),
        h('div', { style: 'display:flex;gap:1rem;flex-wrap:wrap;margin-top:.5rem' },
            promo.views
                ? h('span', { className: 'promo-detail__expires', style: 'background:#eef2ff;color:#6366f1' }, `👁 ${promo.views} visitas`)
                : null,
            promo.expires
                ? h('span', { className: 'promo-detail__expires' }, `⏳ Válido hasta: ${promo.expires}`)
                : null
        )
    )

    /* Contacto — campos estructurados del JSON + extraído de la descripción */
    const contactItems = []
    const ci = (label, value) => contactItems.push(h('div', { className: 'promo-detail__contact-item' },
        h('span', { className: 'promo-detail__contact-label' }, label),
        h('span', { className: 'promo-detail__contact-value' }, value)
    ))

    if (promo.direccion) ci('Dirección', promo.direccion)
    if (promo.barrio)    ci('Barrio', promo.barrio)
    if (promo.telefono)  ci('Teléfono', promo.telefono)

    parsed.contact.forEach(line => {
        const match = line.match(/^(.+?):\s*(.+)/)
        if (match) {
            ci(match[1].trim(), match[2].trim())
        } else {
            contactItems.push(h('div', { className: 'promo-detail__contact-item' },
                h('span', { className: 'promo-detail__contact-value' }, line)
            ))
        }
    })

    /* Redes sociales */
    const socialLinks = []
    const waNum = promo.whatsapp || (business && business.whatsapp)
    if (waNum) socialLinks.push(h('a', { href: waLink(waNum), target: '_blank', rel: 'noopener', className: 'promo-detail__social promo-detail__social--wa', innerHTML: uiIcons.whatsapp + ' WhatsApp' }))
    if (promo.facebook) socialLinks.push(h('a', { href: promo.facebook, target: '_blank', rel: 'noopener', className: 'promo-detail__social promo-detail__social--fb', innerHTML: brandIcons.facebook + ' Facebook' }))
    if (promo.instagram) socialLinks.push(h('a', { href: promo.instagram, target: '_blank', rel: 'noopener', className: 'promo-detail__social promo-detail__social--ig', innerHTML: brandIcons.instagram + ' Instagram' }))
    if (promo.tiktok) socialLinks.push(h('a', { href: promo.tiktok, target: '_blank', rel: 'noopener', className: 'promo-detail__social promo-detail__social--tt', innerHTML: brandIcons.tiktok + ' TikTok' }))

    const hasContact = contactItems.length || socialLinks.length
    const contactSection = hasContact
        ? h('div', { className: 'promo-detail__contact' },
            h('h3', { className: 'promo-detail__contact-title' }, '📍 Contacto e información'),
            contactItems.length
                ? h('div', { className: 'promo-detail__contact-list' }, ...contactItems)
                : null,
            socialLinks.length
                ? h('div', { className: 'promo-detail__socials' }, ...socialLinks)
                : null
        )
        : null

    /* CTA */
    const cta = h('div', { className: 'promo-detail__cta' },
        h('div', { className: 'promo-detail__cta-text' },
            h('h3', {}, '¿Te interesa esta promoción?'),
            h('p',  {}, `Contacta a ${promo.business} directamente.`)
        ),
        h('div', { className: 'promo-detail__cta-actions' },
            waNum
                ? h('a', {
                    href:      waLink(waNum),
                    target:    '_blank',
                    rel:       'noopener',
                    className: 'promo-detail__btn promo-detail__btn--primary',
                    innerHTML: uiIcons.whatsapp + ' Contactar por WhatsApp' })
                : null,
            business
                ? h('a', {
                    href:      `/negocio/${promo.businessId}`,
                    className: 'promo-detail__btn promo-detail__btn--outline'
                }, 'Ver negocio completo →')
                : null
        )
    )

    return h('div', { className: 'page' },
        c('Header'),
        h('div', { className: 'promo-detail-page' },
            h('div', { className: 'promo-detail__inner' },
                h('a', { href: '/promociones', className: 'promo-detail__back' }, '← Volver a promociones'),
                h('div', { className: 'promo-detail__card' },
                    heroSection,
                    body
                ),
                contactSection,
                cta
            )
        )
    )
})
